import {useContext, useEffect, useRef, useState} from "react";
import {AuthContext} from "../context/AuthContext";
import {Link, useNavigate} from "react-router-dom";
import CreditBalanceBadge from "./CreditBalanceBadge";
import {FaDesktop, FaMoon, FaSun} from "react-icons/fa";
import TimezoneInfo from "./TimezoneInfo";
import NotificationButton from "./NotificationButton";
import axiosInstance from "../utils/axiosInstance";
import Notification from "./Notification";
import {TbRefresh} from "react-icons/tb";
import {Bars3Icon, ChevronDownIcon} from "@heroicons/react/24/outline";
import {CONTROL, PANEL, buttonClass} from "./ui";

const Header = ({toggleSidebar}) => {
    const {
        updateUser,
        isAuthenticated,
        user,
        logout,
        isInstallable,
        handleInstall,
    } = useContext(AuthContext);
    // Dark unless the account has opted out — the same default index.js applies
    // before the first paint, so the toggle never disagrees with the canvas.
    const [isDarkMode, setIsDarkMode] = useState(() => {
        try {
            const savedMode = localStorage.getItem("darkMode");
            return savedMode === null ? true : JSON.parse(savedMode);
        } catch {
            return true;
        }
    });
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [accountSettingsPath, setAccountSettingsPath] = useState("");
    const [showNotification, setShowNotification] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const navigate = useNavigate();
    const dropdownRef = useRef(null);

    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const response = await axiosInstance.get("/users/my");
                setNotifications(response.data.notifications);
                const currentCreditBalance = user.credit_balance;

                if (currentCreditBalance !== response.data.credit_balance) {
                    const newUser = {
                        ...user,
                        credit_balance: Number(response.data.credit_balance),
                    };
                    updateUser(newUser);
                    setShowNotification(true);
                }
                const currentAiCreditBalance = user.ai_credit_balance;
                if (currentAiCreditBalance !== response.data.ai_credit_balance) {
                    const newUser = {
                        ...user,
                        ai_credit_balance: Number(response.data.ai_credit_balance),
                    };
                    updateUser(newUser);
                    setShowNotification(true);
                }
            } catch (error) {
                console.error("Error fetching user data:", error);
            }
        };

        const intervalId = setInterval(fetchUserData, 5000);

        return () => clearInterval(intervalId);
    }, [user]);

    useEffect(() => {
        setAccountSettingsPath(
            user.role === "admin" ? "/admin/account-settings" : "/account-settings"
        );

        if (isDarkMode) {
            document.documentElement.classList.add("dark");
        } else {
            document.documentElement.classList.remove("dark");
        }

        localStorage.setItem("darkMode", JSON.stringify(isDarkMode));
    }, [isDarkMode]);

    const toggleDarkMode = () => {
        setIsDarkMode((prevMode) => !prevMode);
    };

    const toggleDropdown = () => {
        setIsDropdownOpen(!isDropdownOpen);
    };

    const handleLogout = () => {
        logout(navigate);
    };

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false); // Close the dropdown if clicked outside
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleHardRefresh = () => {
        window.location.href =
            window.location.href.split("?")[0] + "?t=" + new Date().getTime();
    };

    // Every round control in the bar shares this shape.
    const iconControl = `inline-flex h-10 w-10 items-center justify-center rounded-full border transition hover:border-red-400/60 hover:text-red-500 ${CONTROL}`;

    return (
        <header className="sticky top-0 z-20 border-b border-gray-200 bg-white/90 px-4 py-3 backdrop-blur dark:border-[#2c2c2e] dark:bg-[#1c1c1e]/90 sm:px-6">
            <div className="flex items-center gap-3">
                <button
                    onClick={toggleSidebar}
                    aria-label="Open menu"
                    className={`${iconControl} md:hidden`}
                >
                    <Bars3Icon className="h-5 w-5"/>
                </button>

                {/* Add to Desktop Shortcut Button */}
                {isInstallable && (
                    <button onClick={handleInstall} className={buttonClass({size: "sm"})}>
                        <FaDesktop/>
                        <span className="hidden lg:block">Install Desktop App</span>
                    </button>
                )}

                <div className="ml-auto flex items-center gap-2 sm:gap-3">
                    <TimezoneInfo adminTimezone="Europe/Skopje"/>
                    {user.role !== "admin" && (
                        <CreditBalanceBadge balance={user?.credit_balance || 0}/>
                    )}

                    <NotificationButton
                        userId={user.id}
                        notifications={notifications}
                        setNotifications={setNotifications}
                    />

                    {/* Hard Refresh Button */}
                    <button
                        onClick={handleHardRefresh}
                        aria-label="Reload"
                        title="Reload"
                        className={iconControl}
                    >
                        <TbRefresh size={20}/>
                    </button>

                    {/* User Dropdown */}
                    <div className="relative" ref={dropdownRef}>
                        <button
                            onClick={toggleDropdown}
                            className={`flex h-10 items-center gap-2 rounded-full border pl-1 pr-3 transition hover:border-red-400/60 ${CONTROL}`}
                        >
                            <span
                                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-red-500 to-orange-400 text-xs font-bold uppercase text-white">
                                {(isAuthenticated && user?.username ? user.username : "G").slice(0, 2)}
                            </span>
                            <span className="hidden max-w-[140px] truncate text-sm font-semibold sm:block">
                                {isAuthenticated ? user && user.username : "Guest"}
                            </span>
                            <ChevronDownIcon
                                className={`h-4 w-4 shrink-0 transition-transform ${
                                    isDropdownOpen ? "rotate-180" : "rotate-0"
                                }`}
                            />
                        </button>

                        {isDropdownOpen && (
                            <div
                                className={`absolute right-0 z-30 mt-2 w-52 overflow-hidden p-1.5 ${PANEL}`}
                            >
                                {user.role !== "admin" && (
                                    <button
                                        onClick={toggleDarkMode}
                                        className="flex w-full items-center gap-2.5 rounded-[10px] px-3 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-100 dark:text-slate-200 dark:hover:bg-[#262628]"
                                    >
                                        {isDarkMode ? (
                                            <>
                                                <FaSun className="h-4 w-4 text-amber-400"/>
                                                <span>Light Mode</span>
                                            </>
                                        ) : (
                                            <>
                                                <FaMoon className="h-4 w-4 text-slate-500 dark:text-slate-300"/>
                                                <span>Dark Mode</span>
                                            </>
                                        )}
                                    </button>
                                )}

                                {isAuthenticated && (
                                    <>
                                        <Link
                                            to={accountSettingsPath}
                                            onClick={() => setIsDropdownOpen(false)}
                                            className="block rounded-[10px] px-3 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-100 dark:text-slate-200 dark:hover:bg-[#262628]"
                                        >
                                            Account Settings
                                        </Link>

                                        <button
                                            onClick={handleLogout}
                                            className="block w-full rounded-[10px] px-3 py-2.5 text-left text-sm font-semibold text-rose-600 transition hover:bg-rose-500/10 dark:text-rose-400"
                                        >
                                            Logout
                                        </button>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {showNotification && (
                <Notification
                    setShowNotification={setShowNotification}
                    message={"Your credit balance is updated"}
                    isError={false}
                />
            )}
        </header>
    );
};

export default Header;
